import React, { memo, Fragment, useState, useEffect, useCallback } from "react";
import { Row, Col, Button, Modal, Form, Nav, Tab } from "react-bootstrap";
import Select from "react-select";
import Card from "../../../components/bootstrap/card";
import FullCalendar from "@fullcalendar/react";
import koLocale from "@fullcalendar/core/locales/ko";
import Flatpickr from "react-flatpickr";
import { Korean } from "flatpickr/dist/l10n/ko.js";
import dayGridPlugin from "@fullcalendar/daygrid";
import listPlugin from "@fullcalendar/list";
import bootstrapPlugin from "@fullcalendar/bootstrap";
import * as moment from "moment";
import useLessonLists from "../../../hooks/lesson/useLessonLists";
import { deleteLesson, getLesson, updateLesson } from "../../../api/lesson";
import { getAllUserByStoreId, getUserMe } from "../../../api/auth";
import useUserMe from "../../../hooks/useUserMe";
import { getConfigLessonList } from "../../../api/config";
import useInstructorLists from "../../../hooks/useInstructorList";
import useInstructorLessonMemoList from "../../../hooks/lesson/useInstructorLessonMemoList";
import { updateInstructorMemo } from "../../../api/instructor";
import '../../../assets/custom/legend-badge.css';

const getCurrentSearchDate = () => {
  const now = new Date();
  return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
};

const transformLessonLists = (lessonLists) => lessonLists.map((lesson) => {
  let backgroundColor, textColor, borderColor, className = '';

  // 예약 및 대기 인원 계산
  const reserveCount = lesson.lessonUserList.filter(user => user.logStatus === 'RESERVE').length;
  const waitingCount = lesson.lessonUserList.filter(user => user.logStatus === 'WAIT').length;

  switch (lesson.lessonType) {
    case 'GROUP':
      if (lesson.status === 'FINISH') { // 그룹레슨 상태마감
        className = 'legend-group-finish';
      } else if (lesson.count && reserveCount === lesson.count) { // 그룹레슨 인원마감
        className = 'legend-group-full';
      } else if (reserveCount < lesson.count && waitingCount > 0) { // 그룹레슨 지점확인 (대기 인원 있음)
        textColor = '#fff';
        className = 'legend-group-waiting';
      } else { // 그룹레슨 오픈
        textColor = '#198754';
        className = 'legend-group';
      }
      break;
    case 'INDIVIDUAL':
      if (lesson.status === 'FINISH') { // 개인레슨 상태마감
        textColor = '#fff';
        className = 'legend-individual-finish';
      } else { // 개인레슨 오픈
        textColor = '#0d6efd';
        className = 'legend-individual';
      }
      break;
    case 'CONSULT': // 상담
      textColor = '#f16a1b';
      className = 'legend-consult';
      break;
    default:
      // 예상치 못한 lessonType에 대한 처리 (옵션)
      console.warn('Unknown lesson type:', lesson.lessonType);
      break;
  }
  
  let lessonTitle = lesson.lessonTime;
  if (lesson.lessonType === 'INDIVIDUAL' && lesson.lessonUserList.length > 0) {
    lessonTitle += ' ' + lesson.lessonUserList.map(user => user.name).join(', ');
  } else if (lesson.lessonType === 'GROUP') {
    lessonTitle += lesson.lessonOption ? ` 그룹 (${lesson.lessonOption})` : ' 그룹';
  }
  return {
    id: lesson.id,
    title: lessonTitle,
    start: moment(lesson.lessonDate).format("YYYY-MM-DD"),
    end: moment(lesson.lessonDate).format("YYYY-MM-DD"),
    backgroundColor,
    textColor,
    borderColor,
    className,
  };
});

const getUserSelectOptions = (users) => users.map((user) => ({
  value: user.id,
  label: `${user.name} (${user.phone}) | ${user.userProgram?.individualCount ?? 0}/${user.userProgram?.groupCount ?? 0}`,
  individualCount: user.userProgram?.individualCount ?? 0,
  individualType: user.userProgram?.individualType ?? "",
  groupCount: user.userProgram?.groupCount ?? 0,
}));

const sortWaitUsersByCreatedAt = (waitUsers = []) => [...waitUsers].sort((a, b) => {
  const aCreatedAt = a?.lessonLog?.createdAt || '';
  const bCreatedAt = b?.lessonLog?.createdAt || '';
  const aTime = aCreatedAt ? new Date(aCreatedAt).getTime() : Number.MAX_SAFE_INTEGER;
  const bTime = bCreatedAt ? new Date(bCreatedAt).getTime() : Number.MAX_SAFE_INTEGER;

  if (aTime !== bTime) return aTime - bTime;

  const aLogId = a?.lessonLog?.id ?? Number.MAX_SAFE_INTEGER;
  const bLogId = b?.lessonLog?.id ?? Number.MAX_SAFE_INTEGER;
  return aLogId - bLogId;
});

const ScheduleList = memo(() => {
  // 레슨 모달
  const [show, setShow] = useState(false);
  // 강사메모 모달
  const [memoShow, setMemoShow] = useState(false);

  const { data: userMeData } = useUserMe();
  const storeId = userMeData?.instructor?.storeId;
  const myInstructorId = userMeData?.instructor?.id;
  const [searchDate, setSearchDate] = useState(getCurrentSearchDate());
  const [transformedData, setTransformedData] = useState([]);
  const [lessonData, setLessonData] = useState({
    instructorId: "",
    storeId: "",
    count: 0,
    status: "",
    memo: "",
    lessonType: "",
    lessonTypeName: "",
    lessonOption: "",
    lessonDate: "",
    lessonTime: "",
    lessonUserList: [],
    lessonWaitUserList: [],
    selectUserData: [],
    waitUserData: [],
    noShowUserData: [],
  });
  const [userSelectData, setUserSelectData] = useState();
  const [selectedOption, setSelectedOption] = useState([]);
  const [lessonTypes, setLessonTypes] = useState([]);
  const [lessonOptions, setLessonOptions] = useState([]);
  const [lessonDate, setLessonDate] = useState();
  const [lessonTime, setLessonTime] = useState();
  const [userSelectedData, setUserSelectedData] = useState();
  const [userWaitData, setUserWaitData] = useState();
  const [userNoShowData, setUserNoShowData] = useState();
  const [userRefresh, setUserRefresh] = useState(null);
  const { instructorList } = useInstructorLists(storeId, null, true);
  const [sortedInstructorList, setSortedInstructorList] = useState(instructorList);
  const [allView, setAllView] = useState(false);
  const [groupLessonView, setGroupLessonView] = useState(false);
  const [groupLessonAllView, setGroupLessonAllView] = useState(false);
  const [viewInstructorId, setViewInstructorId] = useState(null);
  const [isViewFetching, setIsViewFetching] = useState(true);
  // storeId, viewInstructorId가 모두 준비된 후에만 useLessonLists 호출
  const shouldFetchLessons = storeId && viewInstructorId;
  const { lessonList, lessonListLoading, refresh, invalidateCache } = useLessonLists(
    shouldFetchLessons ? storeId : null,
    searchDate,
    allView,
    shouldFetchLessons ? viewInstructorId : null,
    groupLessonView,
    groupLessonAllView
  );
  const { instructorMemoList } = useInstructorLessonMemoList(storeId, myInstructorId);
  const [instructorMemo, setInstructorMemo] = useState(null);
  const [instructorMemoData, setInstructorMemoData] = useState({ id: "", instructorId: "", lessonMemo: "" });

  // 핸들러 함수들
  const handleClose = useCallback(() => setShow(false), []);
  const handleShow = useCallback(() => setShow(true), []);
  const handleMemoClose = useCallback(() => setMemoShow(false), []);

  // 강사 정렬 useEffect
  useEffect(() => {
    if (instructorList && myInstructorId) {
      const matchingInstructor = instructorList.find(i => i.id === myInstructorId);
      const remaining = instructorList.filter(i => {
        if (i.id !== myInstructorId) {
          if (matchingInstructor && matchingInstructor.position !== 'DIRECTOR') {
            return i.position !== 'DIRECTOR';
          }
          return true;
        }
        return false;
      });
      setSortedInstructorList(matchingInstructor ? [matchingInstructor, ...remaining] : instructorList);
      // viewInstructorId가 이미 세팅되어 있으면 다시 세팅하지 않음
      if (!viewInstructorId) {
        setViewInstructorId(myInstructorId);
      }
    }
  }, [instructorList, myInstructorId]);

  // 레슨 리스트 변환 useEffect
  useEffect(() => {
    // storeId, lessonList가 모두 준비되고, 로딩이 끝난 후에만 실행
    if (
      storeId &&
      lessonListLoading === false &&
      Array.isArray(lessonList)
    ) {
      setTransformedData(transformLessonLists(lessonList));
      setIsViewFetching(false);
    }
  }, [lessonList, storeId, lessonListLoading]);

  // 레슨 상세 데이터 fetch
  const fetchLessonDetailData = useCallback(async (eventId) => {
    try {
      const lesson = await getLesson(eventId);
      const sortedWaitUserList = sortWaitUsersByCreatedAt(lesson.lessonWaitUserList);
      lesson.selectUserData = lesson.lessonUserList.map(user => ({ value: user.id, label: user.name }));
      lesson.lessonWaitUserList = sortedWaitUserList;
      lesson.waitUserData = sortedWaitUserList.map(user => ({ value: user.id, label: user.name }));
      lesson.noShowUserData = lesson.lessonNoShowUserList.map(user => ({ value: user.id, label: user.name }));
      setLessonData(lesson);
      handleShow();
    } catch (error) {
      console.error("Error fetching event data:", error);
    }
  }, [handleShow]);

  const handleLessonDataShow = useCallback((info) => {
    setUserSelectedData(null);
    setUserNoShowData(null);
    setUserWaitData(null);
    setLessonDate();
    setLessonTime();
    fetchLessonDetailData(info.event.id);
  }, [fetchLessonDetailData]);

  // 회원 검색 셀렉트 useEffect
  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const userData = await getUserMe();
        setUserSelectData(userData);
        setUserRefresh(false);
      } catch (error) {
        console.error("Failed to fetch user data", error);
      }
    };
    fetchUserData();
  }, [userRefresh]);

  // 회원 리스트 옵션 useEffect
  useEffect(() => {
    if (userSelectData && userSelectData.instructor.id) {
      const fetchUserList = async () => {
        try {
          const result = await getAllUserByStoreId(userSelectData.instructor.storeId);
          setSelectedOption(getUserSelectOptions(result));
        } catch (error) {
          console.error("Failed to fetch leave detail", error);
        }
      };
      fetchUserList();
    }
  }, [userSelectData]);

  // 레슨 종류 및 옵션 config useEffect
  useEffect(() => {
    if (userSelectData && userSelectData.instructor.storeId) {
      const fetchConfigData = async () => {
        try {
          const configData = await getConfigLessonList(userSelectData.instructor.storeId);
          const types = configData.reduce((acc, item) => {
            if (!acc.some(type => type.lessonType === item.lessonType)) {
              acc.push({ lessonType: item.lessonType, lessonTypeName: item.lessonTypeName });
            }
            return acc;
          }, []);
          setLessonTypes(types);
          const options = [
            ...new Set(
              configData
                .map(item => ({ lessonOption: item.lessonOption, lessonType: item.lessonType, lessonTypeName: item.lessonTypeName }))
                .filter(option => option.lessonOption !== null && option.lessonOption !== '')
            ),
          ];
          setLessonOptions(options);
        } catch (error) {
          console.error("Failed to fetch getConfigPriceList", error);
        }
      };
      fetchConfigData();
    }
  }, [userSelectData]);

  // 핸들러 함수들 분리
  const handleChange = useCallback((e) => {
    const { name, value, type, checked } = e.target;
    setLessonData(prev => ({ ...prev, [name]: type === "checkbox" ? checked : value }));
  }, []);

  const handleUserSelectedChange = useCallback((target) => {
    setUserSelectedData(target.map(select => ({
      name: select.label,
      value: select.value,
      individualCount: select.individualCount,
      individualType: select.individualType,
      groupCount: select.groupCount
    })));
  }, []);

  const handleUserWaitChange = useCallback((target) => {
    setUserWaitData(target.map(select => ({
      name: select.label,
      value: select.value,
      individualCount: select.individualCount,
      individualType: select.individualType,
      groupCount: select.groupCount
    })));
  }, []);

  const handleUserNoShowChange = useCallback((target) => {
    setUserNoShowData(target.map(select => ({
      name: select.label,
      value: select.value,
      individualCount: select.individualCount,
      individualType: select.individualType,
      groupCount: select.groupCount
    })));
  }, []);

  const handleDateChange = (selectedDates) => {
    const formattedDates = selectedDates.map((date) => {
      const year = date.getFullYear();
      const month = String(date.getMonth() + 1).padStart(2, "0");
      const day = String(date.getDate()).padStart(2, "0");
      return `${year}-${month}-${day}`;
    });
    setLessonDate(formattedDates[0]);
  };

  const handleTimeChange = useCallback((selectedDates) => {
    const selectedTime = selectedDates[0];
    if (!selectedTime) return;
    const hours = selectedTime.getHours().toString().padStart(2, "0");
    const minutes = selectedTime.getMinutes().toString().padStart(2, "0");
    setLessonTime(`${hours}:${minutes}`);
  }, []);

  // 강사 메모 핸들러
  const handleUpdateMemoShow = useCallback((memo, id) => {
    setInstructorMemo(memo);
    setMemoShow(true);
    setInstructorMemoData({ id, instructorId: myInstructorId, lessonMemo: memo });
  }, [myInstructorId]);

  const handleUpdateInstructorMemo = useCallback(async () => {
    try {
      await updateInstructorMemo({ ...instructorMemoData, lessonMemo: instructorMemo });
      alert("수정되었습니다.");
      window.location.reload();
    } catch (error) {
      console.error("Error updating memo:", error.message);
    }
  }, [instructorMemo, instructorMemoData]);

  // 캘린더 날짜 변경 핸들러
  const handleDatesSet = useCallback((dateInfo) => {
    if (dateInfo.view.type === 'dayGridMonth' || dateInfo.view.type === 'dayGridWeek' || dateInfo.view.type === 'dayGridDay') {
      const startDate = dateInfo.start;
      const year = startDate.getFullYear();
      const month = String(startDate.getMonth() + 1).padStart(2, "0");
      const newSearchDate = `${year}-${month}`;
      // searchDate가 실제로 변경되었을 때만 업데이트
      setSearchDate(prev => {
        if (prev !== newSearchDate) {
          return newSearchDate;
        }
        return prev;
      });
    }
  }, []);

  // 레슨 수정/삭제/조회 핸들러
  const handleScheduleEdit = useCallback(async (e) => {
    e.preventDefault();
    if (lessonDate !== undefined) lessonData.lessonDate = lessonDate;
    if (lessonTime !== undefined) lessonData.lessonTime = lessonTime;
    let userValueList = userSelectedData ? userSelectedData.map(u => u.value) : lessonData.lessonUserList.map(u => u.id);
    let waitUserValueList = userWaitData ? userWaitData.map(u => u.value) : lessonData.lessonWaitUserList.map(u => u.id);
    let noShowUserValueList = userNoShowData ? userNoShowData.map(u => u.value) : lessonData.lessonNoShowUserList.map(u => u.id);
    const allValues = [...userValueList, ...waitUserValueList, ...noShowUserValueList];
    const duplicateValues = allValues.filter((v, i, arr) => arr.indexOf(v) !== i);
    if (duplicateValues.length > 0) {
      alert(`참여/대기/노쇼에 중복된 회원이 있습니다. 회원은 한 그룹에만 속해야 합니다.`);
      return false;
    }
    if (!lessonData.lessonType) {
      alert("레슨 종류를 선택해주세요.");
      return false;
    }
    if (!lessonData.lessonDate) {
      alert("날짜를 입력해주세요.");
      return false;
    }
    if (!lessonData.lessonTime) {
      alert("시간을 입력해주세요.");
      return false;
    }
    if (Number(lessonData.count) === 0) {
      alert("인원제한은 최소 1명이상 되어야 합니다.");
      return false;
    }
    if (userSelectedData && Number(lessonData.count) < userSelectedData.length) {
      alert("참여인원이 설정된 인원제한보다 많습니다.");
      return false;
    }
    lessonData.instructorId = userSelectData.instructor.id;
    lessonData.storeId = userSelectData.instructor.storeId;
    if (userSelectedData) {
      userValueList = [];
      for (let select of userSelectedData) {
        const userName = select.name.split('(')[0].trim();
        if (lessonData.lessonType === 'INDIVIDUAL' && select.individualCount === 0) {
          alert(`${userName} 회원은 잔여 개인 횟수가 없습니다.`);
          return false;
        }
        if (lessonData.lessonType === 'GROUP' && select.groupCount === 0) {
          alert(`${userName} 회원은 잔여 그룹 횟수가 없습니다.`);
          return false;
        }
        if (lessonData.lessonType === 'INDIVIDUAL' && lessonData.lessonOption !== '' && lessonData.lessonOption !== select.individualType) {
          alert(`${userName} 회원의 개인레슨 옵션은 ${select.individualType}입니다.`);
          return false;
        }
        userValueList.push(select.value);
      }
    }
    const formLessonData = {
      id: lessonData.id,
      instructorId: lessonData.instructorId,
      lessonType: lessonData.lessonType,
      lessonOption: lessonData.lessonOption,
      count: lessonData.count,
      status: lessonData.status,
      memo: lessonData.memo,
      lessonDate: lessonData.lessonDate,
      lessonTime: lessonData.lessonTime,
      userList: userValueList,
      waitUserList: waitUserValueList,
      noShowUserList: noShowUserValueList,
    };
    try {
      await updateLesson(formLessonData);
      alert("수정되었습니다.");
      setUserRefresh();
      // 레슨 날짜를 기반으로 해당 월의 캐시 무효화
      if (formLessonData.lessonDate) {
        const lessonDateObj = new Date(formLessonData.lessonDate);
        const year = lessonDateObj.getFullYear();
        const month = String(lessonDateObj.getMonth() + 1).padStart(2, "0");
        const monthDate = `${year}-${month}`;
        invalidateCache(monthDate);
      }
      refresh();
      handleClose();
    } catch (error) {
      alert(error.message);
    }
  }, [lessonDate, lessonTime, lessonData, userSelectedData, userWaitData, userNoShowData, userSelectData, refresh, invalidateCache, handleClose]);

  const handleScheduleDelete = useCallback(async (lessonId) => {
    if (window.confirm("삭제하시겠습니까?\n(삭제된 레슨은 확인할 수 없습니다.)")) {
      try {
        // 삭제 전에 레슨 정보를 가져와서 날짜 확인
        let monthDate = null;
        try {
          const lesson = await getLesson(lessonId);
          if (lesson.lessonDate) {
            const lessonDateObj = new Date(lesson.lessonDate);
            const year = lessonDateObj.getFullYear();
            const month = String(lessonDateObj.getMonth() + 1).padStart(2, "0");
            monthDate = `${year}-${month}`;
          }
        } catch (e) {
          console.error("Failed to get lesson date:", e);
        }
        
        await deleteLesson(lessonId);
        alert("삭제되었습니다.");
        
        // 레슨 날짜를 기반으로 해당 월의 캐시 무효화
        if (monthDate) {
          invalidateCache(monthDate);
        }
        refresh();
        handleClose();
      } catch (error) {
        alert(error.message);
      }
    }
  }, [refresh, invalidateCache, handleClose]);

  const handleAllView = useCallback((e) => {
    const { checked } = e.target;
    if (!isViewFetching) {
      setIsViewFetching(true);
      setAllView(checked);
    } else {
      e.target.checked = allView;
      alert('조회중입니다.');
    }
  }, [isViewFetching, allView]);

  const handleViewInstructor = useCallback((id) => {
    if (id !== viewInstructorId) {
      setIsViewFetching(true);
      setViewInstructorId(id);
    }
  }, [viewInstructorId]);

  const handleGroupLessonView = useCallback((e) => {
    const { checked } = e.target;
    if (!isViewFetching) {
      setIsViewFetching(true);
      setGroupLessonView(checked);
    } else {
      e.target.checked = groupLessonView;
      alert('조회중입니다.');
    }
  }, [isViewFetching, groupLessonView]);

  // 강사들이 그룹 레슨을 모아보기 위한 핸들러
  const handleGroupLessonAllView = useCallback((e) => {
    const { checked } = e.target;
    if (!isViewFetching) {
      setIsViewFetching(true);
      setGroupLessonAllView(checked);
    } else {
      e.target.checked = groupLessonAllView;
      alert('조회중입니다.');
    }
  }, [isViewFetching, groupLessonAllView]);

  return (
    <Fragment>
      {/* 전체 로딩 오버레이 */}
      {(isViewFetching || lessonListLoading || !storeId || !Array.isArray(transformedData)) && (
        <div style={{
          position: 'fixed',
          top: 0,
          left: 0,
          width: '100vw',
          height: '100vh',
          background: 'rgba(0,0,0,0.3)',
          zIndex: 2000,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
        }}>
          <div style={{
            background: 'rgba(255,255,255,0.9)',
            borderRadius: '16px',
            padding: '32px 48px',
            boxShadow: '0 2px 16px rgba(0,0,0,0.15)',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
          }}>
            <div className="spinner-border text-primary mb-3" role="status" style={{width: '3rem', height: '3rem'}}>
              <span className="visually-hidden">Loading...</span>
            </div>
            <div style={{fontWeight: 500, color: '#333'}}>레슨 데이터를 불러오는 중입니다...</div>
          </div>
        </div>
      )}
      <Row>
        <Col md="12">
          <Card>
            <Card.Body className="d-flex justify-content-between align-items-center flex-wrap gap-2">
              <div className="card-title mb-0">
                <h4 className="mb-0">레슨 스케줄</h4>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  <span className="legend-badge legend-group">그룹레슨</span>
                  <span className="legend-badge legend-group-full">그룹레슨(인원마감)</span>
                  <span className="legend-badge legend-group-finish">그룹레슨(상태마감)</span>
                  <span className="legend-badge legend-group-waiting">그룹레슨(지점확인)</span>
                  
                </div>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginTop: 8 }}>
                  <span className="legend-badge legend-individual">개인레슨</span>
                  <span className="legend-badge legend-individual-finish">개인레슨(상태마감)</span>
                  <span className="legend-badge legend-consult">상담</span>
                </div>
              </div>
              <div>
              {myInstructorId && (
                <Tab.Container defaultActiveKey={myInstructorId}>
                  <Nav variant="tabs" className="mb-3" id="nav-tab" role="tablist">
                    {sortedInstructorList.map((instructor, index) => (
                      <Nav.Link
                        key={instructor.id}
                        eventKey={instructor.id}
                        aria-selected="false"
                        variant="d-flex align-items-center"
                        type="button"
                        role="tab"
                        onClick={() => handleViewInstructor(instructor.id)} // Add onClick event here
                      >
                        {instructor.user.name}
                      </Nav.Link>
                    ))}
                  </Nav>
                </Tab.Container>
              )}
              {userMeData && userMeData.instructor.position === "DIRECTOR" && (
                <>
                <Form.Check className="form-switch">
                  <Form.Check.Input type="checkbox" className="me-2" onChange={handleAllView} checked={allView} />
                  <Form.Check.Label htmlFor="trialCheckDefault">
                    전체 스케줄 보기
                  </Form.Check.Label>
                </Form.Check>
                <Form.Check className="form-switch">
                  <Form.Check.Input type="checkbox" className="me-2" onChange={handleGroupLessonView} checked={groupLessonView} />
                  <Form.Check.Label htmlFor="trialCheckDefault">
                    그룹레슨만 보기
                  </Form.Check.Label>
                </Form.Check>
                </>
              )}
              {userMeData && userMeData.instructor.position !== "DIRECTOR" && (
                <Form.Check className="form-switch">
                  <Form.Check.Input type="checkbox" className="me-2" onChange={handleGroupLessonAllView} checked={groupLessonAllView} />
                  <Form.Check.Label htmlFor="trialCheckDefault">
                    그룹레슨 모아보기
                  </Form.Check.Label>
                </Form.Check>
              )}
              </div>
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <Modal show={show} onHide={handleClose}>
        <Modal.Header closeButton>
          <Modal.Title as="h5">레슨 상세</Modal.Title>
          <span style={{color: '#fff'}}>{lessonData.lessonPrice}</span>
        </Modal.Header>
        <Modal.Body>
          <Form onSubmit={handleScheduleEdit}>
            <div className="row">
              <Form.Group className="col-md-6 form-group">
                <Form.Label>레슨날짜</Form.Label>
                <div className="form-group">
                  <Flatpickr
                    options={{
                      dateFormat: "Y-m-d",
                      locale: Korean,
                      defaultDate: lessonData.lessonDate,
                    }}
                    className="form-control flatpickrdate"
                    placeholder="날짜를 선택해주세요."
                    onChange={handleDateChange}
                    required
                  />
                </div>
              </Form.Group>
              <Form.Group className="col-md-6 form-group">
                <Form.Label>레슨시간</Form.Label>
                <div className="form-group">
                  <Flatpickr
                    options={{
                      enableTime: true,
                      noCalendar: true,
                      dateFormat: "H:i",
                      locale: "ko",
                      defaultDate: lessonData.lessonTime,
                    }}
                    className="form-control flatpickrtime"
                    placeholder="시간을 선택해주세요."
                    onChange={handleTimeChange}
                    required
                  />
                </div>
              </Form.Group>
              <Form.Group className="col-md-6 form-group">
                <Form.Label htmlFor="typeSelect">레슨 종류</Form.Label>
                <select
                  className="form-select"
                  id="typeSelect"
                  name="lessonType"
                  value={lessonData.lessonType}
                  onChange={handleChange}
                  required
                >
                  <option value="">선택해주세요.</option>
                  {lessonTypes.map((type, index) => (
                    <option key={index} value={type.lessonType}>
                      {type.lessonTypeName}
                    </option>
                  ))}
                </select>
              </Form.Group>
              <Form.Group className="col-md-6 form-group">
                <Form.Label htmlFor="typeSelect">레슨 옵션</Form.Label>
                <select
                  className="form-select"
                  id="typeSelect"
                  name="lessonOption"
                  value={lessonData.lessonOption}
                  onChange={handleChange}
                >
                  <option value="">선택해주세요.</option>
                  {lessonOptions.map((option, index) => (
                    <option key={index} value={option.lessonOption}>
                      ({option.lessonTypeName}) {option.lessonOption}
                    </option>
                  ))}
                </select>
              </Form.Group>
              <Form.Group className="col-md-12 form-group">
                <Form.Label htmlFor="count">인원제한</Form.Label>
                <Form.Control
                  type="number"
                  name="count"
                  value={lessonData.count}
                  onChange={handleChange}
                  required
                />
              </Form.Group>
              <Form.Group className="col-md-12 form-group">
                <Form.Label htmlFor="count">레슨 상태</Form.Label>
                <select
                  className="form-select"
                  id="typeSelect"
                  name="status"
                  value={lessonData.status}
                  onChange={handleChange}
                >
                  <option value="OPEN">오픈</option>
                  <option value="FINISH">마감</option>
                </select>
              </Form.Group>
              <Form.Group className="col-md-12 form-group">
                <Form.Label>참여 회원</Form.Label>
                <Select
                  defaultValue={lessonData.selectUserData}
                  onChange={handleUserSelectedChange}
                  options={selectedOption}
                  placeholder="선택해주세요."
                  className="basic-multi-select"
                  classNamePrefix="select"
                  isMulti
                />
              </Form.Group>
              <Form.Group className="col-md-12 form-group">
                <Form.Label>대기 회원</Form.Label>
                <Select
                  defaultValue={lessonData.waitUserData}
                  onChange={handleUserWaitChange}
                  options={selectedOption}
                  placeholder="선택해주세요."
                  className="basic-multi-select"
                  classNamePrefix="select"
                  isMulti
                />
              </Form.Group>
              <Form.Group className="col-md-12 form-group">
                <Form.Label style={{color: '#c03221'}}>노쇼 회원</Form.Label>
                <Select
                  defaultValue={lessonData.noShowUserData}
                  onChange={handleUserNoShowChange}
                  options={selectedOption}
                  placeholder="선택해주세요."
                  className="basic-multi-select"
                  classNamePrefix="select"
                  isMulti
                />
              </Form.Group>
              <Form.Group className="form-group">
                <Form.Label htmlFor="lname">메모</Form.Label>
                <Form.Control
                  as="textarea"
                  name="memo"
                  value={lessonData.memo}
                  onChange={handleChange}
                  rows="5"
                />
              </Form.Group>
            </div>
          </Form>
        </Modal.Body>
        <Modal.Footer>
          <Button variant="primary" onClick={handleScheduleEdit}>
            저장
          </Button>
          <Button variant="danger" onClick={() => handleScheduleDelete(`${lessonData.id}`)}>
            삭제
          </Button>
        </Modal.Footer>
      </Modal>
      <Row>
        <Col lg="12">
          <Row>
            <Col lg="12" className="col-lg-12">
              <Card>
                <Card.Body>
                <FullCalendar
                  plugins={[dayGridPlugin, listPlugin, bootstrapPlugin]}
                  locale={koLocale}
                  headerToolbar={{
                    left: 'today',
                    center: 'prev,title,next',
                    right: 'dayGridMonth,dayGridWeek,dayGridDay,listWeek',
                  }}
                  //initialView={isMobile ? 'dayGridWeek' : 'dayGridWeek'}
                  events={transformedData}
                  eventClick={handleLessonDataShow}
                  eventClassNames={(arg) => arg.event.classNames}
                  datesSet={handleDatesSet}
                  // height={isMobile ? 'auto' : 'auto'}
                  // contentHeight={isMobile ? 'auto' : 'auto'}
                />
                </Card.Body>
              </Card>
            </Col>
          </Row>
          {/* 강사 메모 */}
          <Row>
            {instructorMemoList && instructorMemoList
            .filter(data => data.user.active === true)
            .map((data, index) => (
              <Col lg="4" key={data.id}>
                <Card>
                  <Card.Header className="d-flex justify-content-between">
                    <div className="header-title">
                      <h4 className="card-title">
                        {data.user.name}님 메모 {" "}
                        {myInstructorId === data.instructorId && (
                          <Button type="button" variant="btn btn-sm btn-secondary" onClick={() => handleUpdateMemoShow(data.lessonMemo, data.id)}>
                            수정
                          </Button>
                        )}  
                      </h4>
                    </div>
                  </Card.Header>
                  <Card.Body>
                  <Form>
                    <Row>
                      <Col>
                        {/* 메모는 보여주기로 사용하고 수정 기능은 모달을 사용하자 */}
                        <Form.Group className="form-group">
                          <Form.Control
                            as="textarea"
                            name="memo"
                            value={data.lessonMemo}
                            disabled
                            rows="5"
                          />
                        </Form.Group>
                      </Col>
                    </Row>
                  </Form>
                  </Card.Body>
                </Card>
              </Col>
            ))}
            <Modal show={memoShow} onHide={handleMemoClose}>
              <Modal.Header closeButton>
                <Modal.Title as="h5">메모 수정</Modal.Title>
              </Modal.Header>
              <Modal.Body>
                <Form>
                  <Form.Group className="col-md-12 form-group">
                    <Form.Control
                      as="textarea"
                      value={instructorMemo}
                      onChange={(e) => setInstructorMemo(e.target.value)}
                      rows="6"
                    />
                  </Form.Group>
                </Form>
              </Modal.Body>
              <Modal.Footer>
                <Button variant="primary" onClick={handleUpdateInstructorMemo}>
                  저장
                </Button>
              </Modal.Footer>
            </Modal>
          </Row>
        </Col>
      </Row>
    </Fragment>
  );
});

ScheduleList.displayName = "ScheduleList";
export default ScheduleList;
